// Header.js - Top header component
import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { RichCoinIcon } from '../assets/icons/Icons';
import { Icon } from '@iconify/react';
import { APP_CONFIG } from '../config/app';
import SettingsModal from './SettingsModal';
import WalletModal from './WalletModal';
import { TelegramUserContext } from '../App';
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';


// ----------------------------
// CONFIGURATION
// ----------------------------

const HEADER_ICONS = {
  add: {
    id: 'add',
    name: 'Add',
    icon: 'material-symbols:add',
    color: '#ffffff',
    size: '18px'
  },
  settings: {
    id: 'settings',
    name: 'Settings',
    icon: 'uil:setting',
    color: '#000000',
    size: '26px'
  },
  wallet: {
    id: 'wallet',
    name: 'Wallet',
    icon: 'iconoir:wallet-solid',
    color: '#000000',
    size: '26px'
  }
};

// Unified Header Container
const FixedHeader = styled.header`
  width: 100%;
  background-color: #ffffff;
  padding: 0 clamp(12px, 3vw, 20px) 10px;
  padding-top: var(--tg-safe-area-inset-top, 10px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 10px; /* Space between top and bottom parts */
  border-bottom: 1px solid #f0f0f0; /* Optional: adds a subtle separator */
  flex-shrink: 0; /* Prevent header from shrinking in the flex container */
`;

const TopPart = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3px;
  width: 100%;
  padding-top: 5px; /* Add some space at the top */
`;

const BottomPart = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;
`;

// Header Elements
const AppTitle = styled.div`
  font-size: clamp(14px, 4vw, 18px);
  font-weight: bold;
  margin-left: clamp(4px, 1vw, 8px);
`;

const VerifiedIconWrapper = styled.div`
  transform: scale(0.8) translateY(2px);
`;

// Button Styling
const ButtonIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  
  svg {
    width: clamp(22px, 5.5vw, 26px);
    height: clamp(22px, 5.5vw, 26px);
  }
`;

// Coin Info Section
const CoinSection = styled.div`
  display: flex;
  align-items: center;
  padding: 3px 6px;
  gap: 3px;
  background-color: ${props => props.$background || 'transparent'};
  border-radius: 12px;
  
  &.auto-margin {
    margin-left: auto;
  }
`;

const CoinValue = styled.div`
  font-weight: bold;
  font-size: 13px;
`;

const AddButton = styled.button`
  background-color: black;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  padding: 0;
  cursor: pointer;
`;

const SecondaryCoinImage = styled.img`
  width: 22px;
  height: 22px;
  border-radius: 50%;
`;

// Using React.memo to prevent unnecessary re-renders
const Header = React.memo(React.forwardRef(({ secondaryCoinBalance, primaryCoinBalance, onPrimaryCoinClick }, ref) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const telegramUser = useContext(TelegramUserContext);
  const wallet = useTonWallet();
  const [tonConnectUI] = useTonConnectUI();

  const handleSettingsClick = () => {
    setIsSettingsOpen(true);
  };

    const handleWalletClick = async () => {
    if (wallet) {
      setIsWalletModalOpen(true);
      return;
    }

    try {
      // Open the wallet connection modal
      await tonConnectUI.openModal();
      
      // Optional: Add a small delay to ensure the modal is fully open
      await new Promise(resolve => setTimeout(resolve, 300));
    } catch (error) {
      // Handle the case when the user cancels the connection
      if (error.message.includes('aborted') || error.message.includes('cancelled')) {
        // User cancelled, do nothing or log minimally if needed for analytics
        return;
      }
      
      // Log other errors
      console.error('Error connecting wallet:', error);
    }
  };

  const handleDisconnect = async () => {
    await tonConnectUI.disconnect();
    setIsWalletModalOpen(false);
  };

  return (
    <>
      <SettingsModal 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)} 
        userId={telegramUser?.id}
      />

      {wallet && (
        <WalletModal
            isOpen={isWalletModalOpen}
            onClose={() => setIsWalletModalOpen(false)}
            walletAddress={wallet.account.address}
            onDisconnect={handleDisconnect}
        />
      )}

      <FixedHeader ref={ref}>
        <TopPart>
          <RichCoinIcon width="30" height="30" />
          <AppTitle>{APP_CONFIG.APP_NAME}</AppTitle>
          <VerifiedIconWrapper>
            <Icon icon="material-symbols:verified" width="24" height="24" color="#1DA1F2" />
          </VerifiedIconWrapper>
        </TopPart>
        <BottomPart>
          <CoinSection>
            <RichCoinIcon width="35" height="35" />
            <CoinValue>{primaryCoinBalance ? primaryCoinBalance.toFixed(0) : '0'}</CoinValue>
            <AddButton onClick={onPrimaryCoinClick}>
              <Icon 
                icon={HEADER_ICONS.add.icon}
                color={HEADER_ICONS.add.color}
                width={HEADER_ICONS.add.size}
                height={HEADER_ICONS.add.size}
              />
            </AddButton>
          </CoinSection>
          <CoinSection $background="#f5f5f5" className="auto-margin">
            <SecondaryCoinImage src={APP_CONFIG.SECONDARY_COIN_IMAGE_PATH} alt={APP_CONFIG.SECONDARY_COIN_NAME} />
            <CoinValue>{secondaryCoinBalance ? secondaryCoinBalance.toFixed(2) : '0.00'}</CoinValue>
          </CoinSection>
          
          <ButtonIcon onClick={handleSettingsClick}>
            <Icon 
              icon={HEADER_ICONS.settings.icon}
              color={HEADER_ICONS.settings.color}
              width={HEADER_ICONS.settings.size}
              height={HEADER_ICONS.settings.size}
            />
          </ButtonIcon>
          
          <ButtonIcon onClick={handleWalletClick}>
            <Icon 
              icon={HEADER_ICONS.wallet.icon}
              color={HEADER_ICONS.wallet.color}
              width={HEADER_ICONS.wallet.size}
              height={HEADER_ICONS.wallet.size}
            />
          </ButtonIcon>
        </BottomPart>
      </FixedHeader>
    </>
  );
}));

export default Header;
